import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'wouter';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useEstimates } from '../hooks/use-estimates';
import { useServices, type ServiceType } from '../hooks/use-services';
import { useAuth } from '../hooks/use-auth';
import { Plus, Trash2, Calendar, Clock, User, FileText, Calculator, Sparkles, Loader2, Wrench } from 'lucide-react';
import { formatCurrency } from '../lib/utils';

// Layout Components
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import { AddServiceDialog } from '../components/estimates/add-service-dialog';
import { ESTIMATE_UNIT_OPTIONS, formatEstimateUnitLabel } from '../lib/estimate-units';
import { buildAiUserDescription, parseLineItemQuantity } from '../lib/ai-description-input';

const estimateItemSchema = z.object({
  serviceType: z.string().min(1, "Service type is required"),
  description: z.string().min(1, "Description is required"),
  quantity: z.number().min(0.01, "Quantity must be greater than 0"),
  unit: z.string().min(1, "Unit is required"),
  unitPrice: z.number().min(0, "Unit price must be 0 or greater"),
  notes: z.string().optional(),
});

const estimateFormSchema = z.object({
  clientId: z.number().min(1, "Client is required"),
  title: z.string().min(1, "Title is required"),
  status: z.enum(["draft", "sent", "accepted", "rejected", "archived"]),
  issueDate: z.string().min(1, "Issue date is required"),
  expiryDate: z.string().min(1, "Expiry date is required"),
  notes: z.string().optional(),
  terms: z.string().min(1, "Terms are required"),
  taxRate: z.number().min(0).max(100),
  discount: z.number().min(0),
  items: z.array(estimateItemSchema).min(1, "At least one item is required"),
});

type EstimateFormValues = z.infer<typeof estimateFormSchema>;

interface Service {
  id: number;
  name: string;
  serviceType: string;
  unit: string;
  laborRate: number;
  laborMethod: string;
}

const AgentEstimateFormPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [generatingDescription, setGeneratingDescription] = useState<number | null>(null);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const addServiceLineRef = useRef<number | null>(null);
  /** Allow 3 AI description generations per estimate (retries); then show message and do not call API */
  const [aiDescriptionCallsUsed, setAiDescriptionCallsUsed] = useState(0);
  const AI_DESCRIPTION_LIMIT_PER_ESTIMATE = 3;

  // Get clientId from URL params
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const clientIdFromUrl = searchParams?.get('clientId') ? parseInt(searchParams.get('clientId')!) : undefined;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<EstimateFormValues>({
    resolver: zodResolver(estimateFormSchema),
    defaultValues: {
      clientId: clientIdFromUrl || 0,
      status: "draft",
      issueDate: new Date().toISOString().split('T')[0],
      expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
      terms: "Payment due within 30 days",
      taxRate: 0,
      discount: 0,
      items: [{ serviceType: "", description: "", quantity: 1, unit: "unit", unitPrice: 0, notes: "" }],
    },
  });

  // Set clientId when it's available from URL
  useEffect(() => {
    if (clientIdFromUrl && clientIdFromUrl > 0) {
      setValue("clientId", clientIdFromUrl);
      console.log('🔍 Set clientId from URL:', clientIdFromUrl);
    }
  }, [clientIdFromUrl, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const items = watch("items");
  const taxRate = watch("taxRate");
  const discount = watch("discount");

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount - discount;

  // Fetch clients
  const { data: clients = [] } = useQuery({
    queryKey: ['/api/protected/clients'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/protected/clients');
      return response.json();
    }
  });

  // Fetch services (+ Custom fallback when catalog is empty or user wants generic line)
  const { services, isLoading: servicesLoading } = useServices();

  const servicesForSelect = useMemo(() => {
    const list: ServiceType[] = [...services];
    if (!list.some((s) => s.value === 'Custom')) {
      list.push({
        id: -1,
        value: 'Custom',
        label: 'Custom / other',
        unit: 'unit',
        type: 'count',
        category: 'Other',
        description: '',
        price: 0,
        laborRate: 0,
        laborMethod: 'by_unit',
      });
    }
    return list;
  }, [services]);

  const { createEstimateMutation } = useEstimates();
  const isCreatingEstimate = createEstimateMutation.isPending;
  // Debug: Monitor description changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.includes('description')) {
        console.log('Description field changed:', { name, value: value.items });
      }
      if (name?.includes('serviceType')) {
        console.log('Service type changed:', { name, value: value.items });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Debug: Log form initialization
  useEffect(() => {
    console.log('Form initialized with values:', getValues());
  }, [getValues]);

  const generateDescription = async (index: number, serviceType: string) => {
    if (aiDescriptionCallsUsed >= AI_DESCRIPTION_LIMIT_PER_ESTIMATE) {
      toast({
        title: 'Limit reached',
        description: `You can generate up to ${AI_DESCRIPTION_LIMIT_PER_ESTIMATE} AI descriptions per estimate. Edit the text manually or create a new estimate.`,
        variant: 'destructive',
      });
      return;
    }

    const item = getValues(`items.${index}`);
    const quantity = parseLineItemQuantity(item?.quantity);
    if (!quantity) {
      toast({
        title: 'Quantity required',
        description: 'Enter the quantity for this line item before generating a description.',
        variant: 'destructive',
      });
      return;
    }

    setGeneratingDescription(index);
    try {
      const svc = servicesForSelect.find(s => s.value === serviceType);
      const name = svc?.label || serviceType || 'Service';
      const unitRaw = item?.unit || svc?.unit || 'unit';
      const unitLabel = formatEstimateUnitLabel(unitRaw);
      const unitPrice = Number(item?.unitPrice) || 0;
      const userDescription = buildAiUserDescription(item?.notes, item?.description);

      const response = await apiRequest('POST', '/api/protected/ai/generate-description', {
        serviceType,
        serviceName: name,
        quantity,
        unit: unitLabel,
        unitPrice,
        userDescription,
      });

      const data = await response.json();
      if (data?.description) {
        setValue(`items.${index}.description`, data.description);
        setAiDescriptionCallsUsed((c) => c + 1);
        if (data.generatedBy === 'fallback') {
          toast({
            title: 'Basic description used',
            description: data.aiError
              ? `AI unavailable (${data.aiError}). A template was used — review before sending.`
              : 'AI was unavailable. A template was used — review and edit before sending.',
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Description generated', description: 'AI has created a professional service description.' });
        }
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      let description = 'Failed to generate description.';
      if (msg.includes('403')) {
        try {
          const json = JSON.parse(msg.replace(/^\d+:\s*/, ''));
          if (json.upgradeRequired) description = 'AI descriptions are available on Pro and Business plans. Upgrade to use this feature.';
          else if (json.message) description = json.message;
        } catch {
          if (msg.includes('Pro') || msg.includes('Business')) description = 'AI descriptions are available on Pro and Business plans.';
        }
      } else if (msg.includes('401')) {
        description = 'Session expired or not logged in. Please sign in again.';
      }
      toast({ title: 'Error', description, variant: 'destructive' });
    } finally {
      setGeneratingDescription(null);
    }
  };

  const onSubmit = (data: EstimateFormValues) => {
    console.log('🔍 DEBUG: onSubmit called with data:', data);
    console.log('🔍 DEBUG: Form validation passed, data is valid');
    
    // Prevent multiple submissions
    if (isCreatingEstimate) {
      console.log('❌ ERROR: Already creating estimate, preventing duplicate submission');
      toast({
        title: "Please Wait",
        description: "Estimate creation is already in progress.",
        variant: "destructive",
      });
      return;
    }
    
    // Check if user is logged in
    if (!user?.id) {
      console.log('❌ ERROR: User not logged in');
      toast({
        title: "Error",
        description: "You must be logged in to create an estimate.",
        variant: "destructive",
      });
      return;
    }
    
    console.log('✅ DEBUG: User is logged in, ID:', user.id);
    
    const totalAmount = data.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    
    // Generate a natural, professional description using service info and user input
    const itemDescriptions = data.items.map(item => {
      // If item has a description (from AI generation), use it directly
      if (item.description && !item.description.includes('installation for')) {
        return item.description;
      }
      // Otherwise create a simple description
      const userInput = item.description || data.notes || '';
      return `Install ${item.serviceType}${userInput ? ` - ${userInput}` : ''} for ${item.quantity} units`;
    });
    
    const aiDescription = itemDescriptions.join('. ') + '. All work performed to professional standards with proper site protection and cleanup.';
    
    const estimateData = {
      clientId: data.clientId,
      estimateNumber: data.title, // Send estimateNumber, not title
      issueDate: data.issueDate,
      expiryDate: data.expiryDate,
      status: data.status,
      subtotal: subtotal.toFixed(2),
      tax: taxRate.toFixed(2),
      discount: discount.toFixed(2),
      total: total.toFixed(2),
      notes: data.notes || '',
      terms: data.terms,
      description: aiDescription, // AI description goes to main description column
      items: data.items.map(item => ({
        serviceType: item.serviceType, // Now properly saving service type
        description: item.description,
        quantity: item.quantity.toString(),
        unit: item.unit || servicesForSelect.find(s => s.value === item.serviceType)?.unit || 'unit',
        unitPrice: item.unitPrice.toFixed(2),
        amount: (item.quantity * item.unitPrice).toFixed(2),
        notes: item.notes || ''
      }))
    };

    console.log('🔍 DEBUG: User ID (contractorId):', user?.id);
    console.log('🔍 DEBUG: Submitting estimate with data:', estimateData);
    console.log('🔍 DEBUG: AI description:', aiDescription);
    console.log('🔍 DEBUG: Items array:', data.items);
    console.log('🔍 DEBUG: Items descriptions:', data.items.map(item => item.description));
    
    console.log('🚀 DEBUG: About to call createEstimateMutation.mutate...');
    console.log('🚀 DEBUG: createEstimateMutation object:', createEstimateMutation);
    
    createEstimateMutation.mutate(estimateData, {
      onSuccess: (data) => {
        console.log('✅ SUCCESS: Estimate created successfully:', data);
        toast({
          title: "Estimate Created",
          description: "Your estimate has been created successfully.",
        });
        setLocation('/estimates');
      },
      onError: (error) => {
        console.error('❌ ERROR: Estimate creation failed:', error);
        console.error('❌ ERROR: Error details:', JSON.stringify(error, null, 2));
        toast({
          title: "Error",
          description: "Failed to create estimate. Please try again.",
          variant: "destructive",
        });
      }
    });
    
    console.log('🚀 DEBUG: createEstimateMutation.mutate called successfully');
  };

  const addItem = () => {
    console.log('Adding new item');
    append({
      serviceType: "",
      quantity: 1,
      unit: "unit",
      unitPrice: 0,
      description: "",
      notes: "",
    });
    console.log('New item added, current items:', watch('items'));
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const handleServiceChange = (index: number, serviceType: string) => {
    console.log('handleServiceChange called:', { index, serviceType });
    setValue(`items.${index}.serviceType`, serviceType);
    
    // Auto-fill unit price from service
    const selectedService = servicesForSelect.find(s => s.value === serviceType);
    if (selectedService) {
      setValue(`items.${index}.unitPrice`, selectedService.laborRate);
      if (selectedService.unit) {
        setValue(`items.${index}.unit`, selectedService.unit);
      }
      console.log('Set unit price to:', selectedService.laborRate);
    }
    
    // IMPORTANT: Do NOT auto-generate description here. Users will click "Generate Description"
    // after entering quantity and price to produce a precise description.
    console.log('No description auto-generation - user must click AI button');
  };

  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="pt-24 px-8 pb-8 space-y-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 min-h-screen">
            <div className="container mx-auto max-w-6xl">
              <div className="remodra-card p-6">
                {/* Header */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="remodra-logo">
                      <Calculator className="h-6 w-6 text-slate-900" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-green-500">Create New Estimate</h1>
                      <p className="text-slate-300">Fill out the details to create a professional estimate for your client.</p>
                    </div>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit(onSubmit, (errors) => {
                  console.log('❌ ERROR: Form validation failed:', errors);
                  console.log('❌ ERROR: Form errors object:', JSON.stringify(errors, null, 2));
                })} className="space-y-8">
                  {/* Client and Basic Info */}
                  <Card className="remodra-card">
                    <CardHeader>
                      <CardTitle className="text-green-500 flex items-center gap-2">
                        <User className="h-5 w-5" />
                        Client Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="clientId" className="text-slate-300 font-medium">Client</Label>
                          <Select
                            onValueChange={(value) => setValue("clientId", parseInt(value))}
                            value={watch("clientId")?.toString() || ""}
                            defaultValue={clientIdFromUrl?.toString() || ""}
                          >
                            <SelectTrigger className="remodra-input">
                              <SelectValue placeholder="Select a client" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients && clients.length > 0 ? (
                                clients.map((client: any) => (
                                  <SelectItem key={client.id} value={client.id.toString()}>
                                    {client.firstName || client.first_name} {client.lastName || client.last_name}
                                  </SelectItem>
                                ))
                              ) : (
                                <div className="px-2 py-1.5 text-sm text-slate-500">No clients available</div>
                              )}
                            </SelectContent>
                          </Select>
                          {errors.clientId && <p className="text-red-400 text-sm mt-1">{errors.clientId.message}</p>}
                        </div>

                        <div>
                          <Label htmlFor="title" className="text-slate-300 font-medium">Estimate Title</Label>
                          <Input
                            id="title"
                            type="text"
                            {...register("title")}
                            className="remodra-input"
                            placeholder="Enter estimate title"
                          />
                          {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title.message}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="status" className="text-slate-300 font-medium">Status</Label>
                          <Select
                            onValueChange={(value) => setValue("status", value as any)}
                            value={watch("status")}
                          >
                            <SelectTrigger className="remodra-input">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="sent">Sent</SelectItem>
                              <SelectItem value="accepted">Accepted</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                              <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Items */}
                  <Card className="remodra-card">
                    <CardHeader>
                      <CardTitle className="text-green-500 flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Estimate Items
                      </CardTitle>
                      <p className="text-slate-500 text-sm mt-1.5">
                        Pick a service, then set quantity, <span className="text-slate-400">unit</span>, and price. Lines are separated below.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-0">
                      <div className="divide-y divide-slate-600/40">
                        {fields.map((field, index) => (
                          <div key={field.id} className="py-6 first:pt-2">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
                              <div className="xl:col-span-1">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <Label className="text-slate-300 font-medium">Service Type</Label>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs shrink-0 border-slate-500 text-slate-200 hover:bg-slate-700"
                                    onClick={() => {
                                      addServiceLineRef.current = index;
                                      setAddServiceOpen(true);
                                    }}
                                  >
                                    <Wrench className="h-3.5 w-3.5 mr-1" />
                                    New service
                                  </Button>
                                </div>
                                <Select
                                  onValueChange={(value) => handleServiceChange(index, value)}
                                  value={watch(`items.${index}.serviceType`) || ""}
                                  disabled={servicesLoading}
                                >
                                  <SelectTrigger className="remodra-input">
                                    <SelectValue placeholder={servicesLoading ? 'Loading services…' : 'Select service type'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {servicesForSelect.map((service) => (
                                      <SelectItem key={`${service.id}-${service.value}`} value={service.value}>
                                        {service.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500 mt-1">
                                  Need something not listed? Use <span className="text-slate-400">New service</span> (saved to your Pricing catalog).
                                </p>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                                <div className="col-span-2 sm:col-span-2">
                                  <Label className="text-slate-300 font-medium">Quantity</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    {...register(`items.${index}.quantity`, { valueAsNumber: true })}
                                    className="remodra-input"
                                  />
                                </div>
                                <div className="col-span-2 sm:col-span-2">
                                  <Label className="text-slate-300 font-medium">Unit</Label>
                                  <Select
                                    value={watch(`items.${index}.unit`) || "unit"}
                                    onValueChange={(value) => setValue(`items.${index}.unit`, value)}
                                  >
                                    <SelectTrigger className="remodra-input">
                                      <SelectValue placeholder="Unit" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[260px]">
                                      {ESTIMATE_UNIT_OPTIONS.map((u) => (
                                        <SelectItem key={u.value} value={u.value}>
                                          {u.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="col-span-2 sm:col-span-2">
                                  <Label className="text-slate-300 font-medium">Unit Price</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                                    className="remodra-input"
                                  />
                                </div>
                                {fields.length > 1 && (
                                  <div className="col-span-2 flex justify-end pb-0.5">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => removeItem(index)}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-4">
                              <div>
                                <Label className="text-slate-300 font-medium">Description</Label>
                                <div className="flex gap-2">
                                  <Textarea
                                    {...register(`items.${index}.description`)}
                                    className="remodra-input flex-1"
                                    placeholder="Service description"
                                    rows={3}
                                  />
                                  {watch(`items.${index}.serviceType`) && (
                                    <div className="flex flex-col items-end gap-1">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => generateDescription(index, watch(`items.${index}.serviceType`))}
                                        disabled={generatingDescription === index || aiDescriptionCallsUsed >= AI_DESCRIPTION_LIMIT_PER_ESTIMATE}
                                        className="text-green-500 hover:text-green-400 border-green-500 hover:border-green-400 hover:bg-green-50 disabled:opacity-60"
                                      >
                                        {generatingDescription === index ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <Sparkles className="h-4 w-4" />
                                        )}
                                      </Button>
                                      {AI_DESCRIPTION_LIMIT_PER_ESTIMATE > 0 && (
                                        <span className="text-xs text-muted-foreground">
                                          AI: {Math.max(0, AI_DESCRIPTION_LIMIT_PER_ESTIMATE - aiDescriptionCallsUsed)} of {AI_DESCRIPTION_LIMIT_PER_ESTIMATE} left
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {errors.items?.[index]?.description && (
                                  <p className="text-red-400 text-sm mt-1">{errors.items?.[index]?.description?.message}</p>
                                )}
                              </div>

                              {/* Service Details Display */}
                              {watch(`items.${index}.serviceType`) && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3 mt-1 border-t border-dashed border-slate-600/35">
                                  <div>
                                    <Label className="text-slate-400 text-xs">Service Name</Label>
                                    <p className="text-slate-200 font-medium">
                                      {servicesForSelect.find(s => s.value === watch(`items.${index}.serviceType`))?.label || 'Unknown Service'}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-slate-400 text-xs">Unit</Label>
                                    <p className="text-slate-200 font-medium">
                                      {ESTIMATE_UNIT_OPTIONS.find((u) => u.value === watch(`items.${index}.unit`))?.label ||
                                        watch(`items.${index}.unit`) ||
                                        servicesForSelect.find((s) => s.value === watch(`items.${index}.serviceType`))?.unit ||
                                        'unit'}
                                    </p>
                                  </div>
                                  <div>
                                    <Label className="text-slate-400 text-xs">Line Total</Label>
                                    <p className="text-slate-200 font-medium">
                                      {formatCurrency((watch(`items.${index}.quantity`) || 0) * (watch(`items.${index}.unitPrice`) || 0))}
                                    </p>
                                  </div>
                                </div>
                              )}

                              <div>
                                <Label className="text-slate-300 font-medium">Notes (Optional)</Label>
                                <Input
                                  {...register(`items.${index}.notes`)}
                                  className="remodra-input"
                                  placeholder="Additional notes for this item"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="pt-4 border-t border-slate-600/40">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addItem}
                          className="remodra-button-outline"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Item
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pricing & Notes */}
                  <Card className="remodra-card">
                    <CardHeader>
                      <CardTitle className="text-green-500 flex items-center gap-2">
                        <Calculator className="h-5 w-5" />
                        Pricing & Notes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="issueDate" className="text-slate-300 font-medium">Issue Date</Label>
                          <Input
                            id="issueDate"
                            type="date"
                            {...register("issueDate")}
                            className="remodra-input"
                          />
                          {errors.issueDate && <p className="text-red-400 text-sm mt-1">{errors.issueDate.message}</p>}
                        </div>
                        <div>
                          <Label htmlFor="expiryDate" className="text-slate-300 font-medium">Expiry Date</Label>
                          <Input
                            id="expiryDate"
                            type="date"
                            {...register("expiryDate")}
                            className="remodra-input"
                          />
                          {errors.expiryDate && <p className="text-red-400 text-sm mt-1">{errors.expiryDate.message}</p>}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="terms" className="text-slate-300 font-medium">Terms</Label>
                        <Input
                          id="terms"
                          {...register("terms")}
                          className="remodra-input"
                          placeholder="Payment due within 30 days"
                        />
                        {errors.terms && <p className="text-red-400 text-sm mt-1">{errors.terms.message}</p>}
                      </div>
                      <div>
                        <Label htmlFor="taxRate" className="text-slate-300 font-medium">Tax Rate (%)</Label>
                        <Input
                          id="taxRate"
                          type="number"
                          step="0.01"
                          {...register("taxRate", { valueAsNumber: true })}
                          className="remodra-input"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label htmlFor="discount" className="text-slate-300 font-medium">Discount ($)</Label>
                        <Input
                          id="discount"
                          type="number"
                          step="0.01"
                          {...register("discount", { valueAsNumber: true })}
                          className="remodra-input"
                          placeholder="0.00"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="notes" className="text-slate-300 font-medium">General Notes</Label>
                        <Textarea
                          id="notes"
                          {...register("notes")}
                          className="remodra-input"
                          placeholder="Additional notes for this estimate"
                          rows={4}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Totals Summary */}
                  <Card className="remodra-card">
                    <CardHeader>
                      <CardTitle className="text-green-500">Estimate Summary</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-slate-300">
                          <span>Subtotal:</span>
                          <span>{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>Tax ({taxRate}%):</span>
                          <span>{formatCurrency(taxAmount)}</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>Discount:</span>
                          <span>-{formatCurrency(discount)}</span>
                        </div>
                        <div className="border-t border-slate-600 pt-3">
                          <div className="flex justify-between text-green-500 font-bold text-lg">
                            <span>Total:</span>
                            <span>{formatCurrency(total)}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Client Preview */}
                  <Card className="remodra-card">
                    <CardHeader>
                      <CardTitle className="text-green-500">Client Preview</CardTitle>
                      <p className="text-slate-400 text-sm">This is how your estimate will appear to the client</p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-400">Issue Date: </span>
                            <span className="text-slate-200">{watch('issueDate')}</span>
                          </div>
                          <div>
                            <span className="text-slate-400">Expiry Date: </span>
                            <span className="text-slate-200">{watch('expiryDate')}</span>
                          </div>
                        </div>
                        
                        <div className="border-t border-slate-600 pt-4">
                          <h4 className="font-medium text-slate-200 mb-3">Services & Items</h4>
                          <div className="space-y-0 divide-y divide-slate-600/40">
                            {items.map((item, index) => (
                              <div key={index} className="py-3 first:pt-0">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <p className="font-medium text-slate-200">
                                      {servicesForSelect.find(s => s.value === item.serviceType)?.label || item.serviceType}
                                    </p>
                                    <p className="text-slate-400 text-sm mt-1 whitespace-pre-line">{item.description}</p>
                                  </div>
                                  <div className="text-right text-sm">
                                    <p className="text-slate-300">{item.quantity} {ESTIMATE_UNIT_OPTIONS.find((u) => u.value === item.unit)?.label || item.unit || servicesForSelect.find((s) => s.value === item.serviceType)?.unit || 'unit'}</p>
                                    <p className="text-slate-300">@ ${item.unitPrice}</p>
                                    <p className="text-green-500 font-medium">{formatCurrency(item.quantity * item.unitPrice)}</p>
                                  </div>
                                </div>
                                {item.notes && (
                                  <p className="text-slate-500 text-xs italic">Note: {item.notes}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {watch('notes') && (
                          <div className="border-t border-slate-600 pt-4">
                            <h4 className="font-medium text-slate-200 mb-2">Additional Notes</h4>
                            <p className="text-slate-400 text-sm">{watch('notes')}</p>
                          </div>
                        )}
                        
                        <div className="border-t border-slate-600 pt-4">
                          <h4 className="font-medium text-slate-200 mb-2">Terms</h4>
                          <p className="text-slate-400 text-sm">{watch('terms')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setLocation('/estimates')} 
                      className="remodra-button-outline"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={isSubmitting || isCreatingEstimate} 
                      className="remodra-button"
                    >
                      {isSubmitting || isCreatingEstimate ? "Creating Estimate..." : "Save Estimate"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </main>
        </div>
      </div>
      <AddServiceDialog
        open={addServiceOpen}
        onOpenChange={(open) => {
          setAddServiceOpen(open);
          if (!open) addServiceLineRef.current = null;
        }}
        onCreated={({ serviceType, laborRate, unit: createdUnit }) => {
          const idx = addServiceLineRef.current;
          if (idx === null) return;
          setValue(`items.${idx}.serviceType`, serviceType);
          setValue(`items.${idx}.unitPrice`, laborRate);
          if (createdUnit) setValue(`items.${idx}.unit`, createdUnit);
          addServiceLineRef.current = null;
        }}
      />
    </div>
  );
};

export default AgentEstimateFormPage;